import { createContext, useContext, useEffect, useRef, useState } from "react";
import Axios from "../Axios";
import $ from "jquery";
import consts from "../consts";

const CommodityContext = createContext(null);

const COMMODITY_WS_URL =
  `wss://ws.twelvedata.com/v1/quotes/price?apikey=${consts.twelvedataApi}`;

const normalizeCommoditySymbol = (symbol = "") =>
  String(symbol).replace(/[_/]/g, "").toUpperCase();

const COMMODITY_WS_SYMBOL_MAP = {
  // Gold
  GOLD: "XAU/USD",
  XAUUSD: "XAU/USD",
  XAU: "XAU/USD",
  XAUUSDT: "XAU/USD",
  // Silver
  SILVER: "XAG/USD",
  XAGUSD: "XAG/USD",
  XAG: "XAG/USD",
  XAGUSDT: "XAG/USD",
  // Platinum
  PLATINUM: "XPT/USD",
  XPTUSD: "XPT/USD",
  XPT: "XPT/USD",
  XPTUSDT: "XPT/USD",
};

const COMMODITY_DOM_MAP = {
  XAUUSD: "GOLD",
  XAU: "GOLD",
  XAUUSDT: "GOLD",
  GOLD: "GOLD",
  XAGUSD: "Silver",
  XAG: "Silver",
  XAGUSDT: "Silver",
  SILVER: "Silver",
  XPTUSD: "Platinum",
  XPT: "Platinum",
  XPTUSDT: "Platinum",
  PLATINUM: "Platinum",
};

const DEFAULT_COMMODITY_PRICES = {
  GOLD: 4029.01,
  Silver: 48.35,
  Platinum: 1580.25,
};

const toCommodityDomKey = (symbol = "") => {
  const normalized = normalizeCommoditySymbol(symbol);
  return COMMODITY_DOM_MAP[normalized] ?? normalized;
};

const toCommodityWsSymbol = (symbol = "") => {
  const normalized = normalizeCommoditySymbol(symbol);
  return COMMODITY_WS_SYMBOL_MAP[normalized] ?? normalized;
};

const formatMarketValue = (rawValue) => {
  const value = Number(rawValue);
  if (!Number.isFinite(value)) return "0.00000";

  if (typeof rawValue === "string" && rawValue.includes(".")) {
    const decimals = Math.min(rawValue.split(".")[1].length, 8);
    return value.toFixed(decimals);
  }

  if (Math.abs(value) >= 1000) return value.toFixed(2);
  if (Math.abs(value) >= 1) return value.toFixed(4);
  return value.toFixed(5);
};

const readCommodityDomPrice = (symbol = "") => {
  const key = toCommodityDomKey(symbol);
  const raw =
    $(`.buybtn-${key}`).text() ||
    $(`.sellbtn-${key}`).text() ||
    $(`.price-${key}`).text() ||
    $(`.buy-${key}`).text() ||
    $(`.sell-${key}`).text();
  const price = Number(String(raw).replace(/,/g, "").trim());
  return Number.isFinite(price) && price > 0 ? price : 0;
};

const getCommodityFallbackPrice = (symbol = "") => {
  const key = toCommodityDomKey(symbol);
  const basePrice = DEFAULT_COMMODITY_PRICES[key];
  if (!basePrice) return 0;

  const now = Date.now();
  const wave =
    Math.sin(now / 45000) * 0.0007 + Math.sin(now / 11000) * 0.0002;
  return Number((basePrice * (1 + wave)).toFixed(basePrice >= 1000 ? 2 : 4));
};

const CommodityProvider = ({ children }) => {
  const socketRef = useRef(null);
  const commodityPriceRef = useRef({});
  const tvWidgetRef = useRef({});
  const lineRefs = useRef({});
  const [symbols, setSymbols] = useState([]); // raw pair objects from API

  // ─── Fetch commodity pairs ────────────────────────────────────────────────
  const getSymbols = async () => {
    try {
      const { data } = await Axios.post(
        "/getPairsByAdmin",
        { type: "commodities" },
        {
          headers: {
            Authorization: localStorage.getItem("nzanzi"),
          },
        },
      );

      if (data?.success) {
        setSymbols(data.result || []);
      } else {
        setSymbols([]);
      }
    } catch (error) {
      setSymbols([]);
      console.log("Commodity symbol fetch error:", error);
    }
  };

  // ─── Parse & store ticker, update DOM ────────────────────────────────────
  const upsertTicker = (symbolCode, price) => {
    const symbol = toCommodityDomKey(symbolCode);
    if (!symbol) return;

    const midprice = Number(price);
    if (!Number.isFinite(midprice) || midprice <= 0) return;

    const spread = 0;

    commodityPriceRef.current[symbol] = {
      symbol,
      bid: midprice,
      ask: midprice,
      buy: midprice,
      sell: midprice,
      spread,
      midprice,
      midPrice: midprice,
      timestamp: Date.now(),
    };

    console.log(
      `[CommodityWS] ${symbol} | price: ${formatMarketValue(midprice)}`,
    );

    $(`.price-${symbol}`).text(formatMarketValue(midprice));
    $(`.buy-${symbol}`).text(formatMarketValue(midprice));
    $(`.sell-${symbol}`).text(formatMarketValue(midprice));
    $(`.spread-${symbol}`).text(formatMarketValue(spread));
    $(`.buybtn-${symbol}`).text(formatMarketValue(midprice));
    $(`.sellbtn-${symbol}`).text(formatMarketValue(midprice));
    $(`.spreadchart-${symbol}`).text(formatMarketValue(spread));
  };

  // ─── Handle incoming AllTick messages ────────────────────────────────────
  const handleMessage = (event) => {
    try {
      const msg = JSON.parse(event.data);

      if (msg?.event === "heartbeat") return;
      if (msg?.event === "subscribe-status") return;
      if (msg?.event !== "price") return;

      const symbol = msg?.symbol;
      const price = msg?.price;
      if (!symbol || price == null) return;

      upsertTicker(symbol, price);
    } catch (e) {
      console.log("[CommodityWS] parse error:", e);
    }
  };

  // ─── Connect & subscribe ─────────────────────────────────────────────────
  const subscribePairs = (pairs) => {
    if (!pairs.length) return undefined;

    let pingInterval = null;
    let socket = null;

    try {
      const symbolList = [...new Set(
        pairs
          .map((item) => toCommodityWsSymbol(item?.pair_symbol))
          .filter(Boolean),
      )];

      socket = new WebSocket(COMMODITY_WS_URL);
      socketRef.current = socket;

      socket.onopen = () => {
        console.log("✅ Connected to Commodity WS");
        const subPayload = {
          action: "subscribe",
          params: {
            symbols: symbolList.join(","),
          },
        };
        console.log("[CommodityWS] Subscription payload:", JSON.stringify(subPayload));
        socket.send(JSON.stringify(subPayload));

        // Heartbeat every 20 s
        pingInterval = setInterval(() => {
          if (socket.readyState === WebSocket.OPEN) {
            socket.send(
              JSON.stringify({
                action: "heartbeat",
              }),
            );
          }
        }, 20000);
      };

      socket.onmessage = handleMessage;

      socket.onerror = (error) => {
        console.error("[CommodityWS] WebSocket error:", error);
      };

      socket.onclose = (event) => {
        console.log("[CommodityWS] WebSocket closed", event.code, event.reason);
        if (pingInterval) clearInterval(pingInterval);
        if (socketRef.current === socket) socketRef.current = null;
      };
    } catch (error) {
      console.log("[CommodityWS] Subscribe error:", error);
    }

    return () => {
      if (pingInterval) clearInterval(pingInterval);
      if (socket) {
        try {
          socket.close();
        } catch (e) {
          console.log("[CommodityWS] Close error:", e);
        }
      }
      if (socketRef.current === socket) socketRef.current = null;
    };
  };

  // ─── Public ticker getter ─────────────────────────────────────────────────
  const getCommodityTicker = (symbol = "") => {
    const key = toCommodityDomKey(symbol);
    if (!key) return null;
    const ticker = commodityPriceRef.current[key];
    if (ticker) return ticker;

    const fallbackPrice = getCommodityFallbackPrice(key);
    if (!fallbackPrice) return null;

    return {
      symbol: key,
      bid: fallbackPrice,
      ask: fallbackPrice,
      buy: fallbackPrice,
      sell: fallbackPrice,
      spread: 0,
      midprice: fallbackPrice,
      midPrice: fallbackPrice,
      timestamp: Date.now(),
    };
  };

  const generateCommodityHistoryBars = (
    symbol = "",
    fromMs = Date.now() - 60 * 60 * 1000,
    toMs = Date.now(),
    resolution = "1",
  ) => {
    const key = toCommodityDomKey(symbol);
    const ticker = key ? commodityPriceRef.current[key] ?? null : null;
    const currentPrice =
      Number(ticker?.midprice ?? ticker?.ask ?? ticker?.bid) ||
      readCommodityDomPrice(key) ||
      getCommodityFallbackPrice(key);
    if (!Number.isFinite(currentPrice) || currentPrice <= 0) return [];

    const map = {
      1: 60 * 1000,
      3: 3 * 60 * 1000,
      5: 5 * 60 * 1000,
      15: 15 * 60 * 1000,
      30: 30 * 60 * 1000,
      60: 60 * 60 * 1000,
      120: 2 * 60 * 60 * 1000,
      240: 4 * 60 * 60 * 1000,
      360: 6 * 60 * 60 * 1000,
      720: 12 * 60 * 60 * 1000,
      "1D": 24 * 60 * 60 * 1000,
      "1W": 7 * 24 * 60 * 60 * 1000,
      "1M": 30 * 24 * 60 * 60 * 1000,
    };

    const step = map[resolution] || 60 * 1000;
    const end = Number(toMs);
    const decimals =
      currentPrice >= 1000 ? 2 : currentPrice >= 100 ? 3 : currentPrice >= 1 ? 4 : 6;

    const close0 = currentPrice * 0.9974;
    const close1 = currentPrice * 0.9983;
    const close2 = currentPrice * 0.9992;
    const close3 = currentPrice;

    const closes = [close0, close1, close2, close3].map((value) =>
      Number(Math.max(0.00001, value).toFixed(decimals)),
    );

    const bars = [];
    const highPad = Math.max(currentPrice * 0.0015, 0.05);
    const lowPad = Math.max(currentPrice * 0.0015, 0.05);

    for (let i = 0; i < 4; i++) {
      const time = end - (3 - i) * step;
      const open =
        i === 0
          ? Number((closes[0] * 1.001).toFixed(decimals))
          : closes[i - 1];
      const close = closes[i];
      const high = Number((Math.max(open, close) + highPad).toFixed(decimals));
      const low = Number(
        Math.max(0.00001, Math.min(open, close) - lowPad).toFixed(decimals),
      );

      bars.push({
        time,
        open,
        high,
        low,
        close,
        volume: Math.floor(120 + i * 30),
      });
    }

    return bars;
  };

  // ─── Chart registration helpers (same pattern as futureContext) ───────────
  const tpSlShapes = useRef({ tp: null, sl: null, bet: null });

  const commodityRegisterChart = (chartId, widget) => {
    tvWidgetRef.current[chartId] = widget;
  };

  const commodityRegisterLines = (chartId, lines) => {
    lineRefs.current[chartId] = lines;
  };

  const commodityUnregisterChart = (chartId) => {
    delete tvWidgetRef.current[chartId];
    delete lineRefs.current[chartId];
  };

  const handleCommodityRemoveAllLines = (chartId) => {
    const widget = tvWidgetRef.current[chartId];
    if (!widget) {
      console.warn(`⚠️ No widget found for chart ${chartId}`);
      return;
    }

    widget.onChartReady(() => {
      const chart = widget.activeChart();
      const shapes = chart.getAllShapes()?.map((shape) => shape?.id);
      try {
        chart.removeAllShapes();
        shapes.forEach((shape) => chart.removeEntity(shape));
        console.log(`🗑️ Removed all lines for chart ${chartId}`);
      } catch (err) {
        console.warn(`❌ Failed to remove shapes for chart ${chartId}`, err);
      }
      delete lineRefs.current[chartId];
    });
  };

  // ─── Lifecycle ────────────────────────────────────────────────────────────
  useEffect(() => {
    getSymbols();
  }, []);

  useEffect(() => {
    if (!symbols.length) return undefined;
    return subscribePairs(symbols);
  }, [symbols]);

  // ─── Provider ─────────────────────────────────────────────────────────────
  return (
    <CommodityContext.Provider
      value={{
        commodityPriceRef,
        getCommodityTicker,
        generateCommodityHistoryBars,
        commodityTvWidgetRef: tvWidgetRef,
        commodityTpslShapes: tpSlShapes,
        commodityLinesRef: lineRefs,
        commodityRegisterLines,
        commodityUnregisterChart,
        commodityRegisterChart,
        handleCommodityRemoveAllLines,
      }}
    >
      {children}
    </CommodityContext.Provider>
  );
};

export const useCommodityData = () => {
  const context = useContext(CommodityContext);
  if (!context)
    throw new Error("useCommodityData must be used within a CommodityProvider");
  return context;
};

export default CommodityProvider;
